import {
  Clear as ClearIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import {
  CreatedProductRecord,
  CreatedProductReportParams,
} from "../../../@types/DRO/AllReportsResponse";
import { GetAllModelTypeArray } from "../../../@types/DRO/GetPartsListResponse";
import { AllReportsApi } from "../../../api/AllReportsDownload/AllReportsApi";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";

const CreatedProductReport: React.FC = () => {
  // State for data and pagination
  const [data, setData] = useState<CreatedProductRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [downloading, setDownloading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // State for filters
  const [filters, setFilters] = useState<CreatedProductReportParams>({
    product_id: undefined,
    description: "",
    oem_type: "",
    status: undefined,
    created_by: "",
    start_date: "",
    end_date: "",
    product_type: "",
    business_type: "",
    page: 1,
    page_size: 50,
  });

  // State for OEM types dropdown
  const [modelTypes, setModelTypes] = useState<GetAllModelTypeArray[]>([]);
  const [selectedOEMType, setSelectedOEMType] =
    useState<GetAllModelTypeArray | null>(null);

  // Fetch model types
  const fetchModelTypes = useCallback(async () => {
    try {
      const response = await AllPartApi.api.getModelTypeAll();
      setModelTypes(response.data);
    } catch (error) {
      console.error("Error fetching model types", error);
    }
  }, []);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: CreatedProductReportParams = {
        ...filters,
        oem_type: selectedOEMType?.description || filters.oem_type,
        page: currentPage,
        page_size: pageSize,
      };

      // Remove empty string values
      Object.keys(params).forEach((key) => {
        const k = key as keyof CreatedProductReportParams;
        if (params[k] === "" || params[k] === undefined) {
          delete params[k];
        }
      });

      const response = await AllReportsApi.api.getCreatedProductReport(params);
      if (response.success) {
        setData(response.records);
        setTotalPages(response.total_pages);
      }
    } catch (err) {
      setError("Failed to fetch product report. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, pageSize, selectedOEMType]);

  // Initial fetch for model types
  useEffect(() => {
    fetchModelTypes();
  }, [fetchModelTypes]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange =
    (field: keyof CreatedProductReportParams) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | { value: unknown }>
        | SelectChangeEvent<string>
    ) => {
      setFilters({
        ...filters,
        [field]: event.target.value,
      });
      setCurrentPage(1); // Reset to first page on filter change
    };

  // Handle search button click
  const handleSearch = () => {
    fetchData();
  };

  const handleNumericInputChange =
    (field: "product_id" | "po_no") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // Allow only numbers and empty string
      if (value === "" || /^[0-9]+$/.test(value)) {
        setFilters({
          ...filters,
          [field]: value === "" ? undefined : parseInt(value, 10),
        });
        setCurrentPage(1);
      }
    };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      product_id: undefined,
      description: "",
      oem_type: "",
      status: undefined,
      created_by: "",
      start_date: "",
      end_date: "",
      product_type: "",
      business_type: "",
    });
    setSelectedOEMType(null);
    setCurrentPage(1);
  };

  // Handle download
  const handleDownload = async () => {
    try {
      setDownloading(true);
      setError(null);

      const params = {
        ...filters,
        oem_type: selectedOEMType?.description || filters.oem_type,
      };
      delete params.page;
      delete params.page_size;
      // Remove empty values
      Object.keys(params).forEach((key) => {
        const k = key as keyof typeof params;
        if (params[k] === "" || params[k] === undefined) {
          delete params[k];
        }
      });

      await AllReportsApi.api.downloadCreatedProductReport(params);
      setSuccessMessage("Report downloaded successfully!");
    } catch (err: any) {
      const errorMessage =
        err.message || "Failed to download report. Please try again.";
      setError(errorMessage);
      console.error("Download error:", err);
    } finally {
      setDownloading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#000" }}>
              Product Master Report
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
              View and export product master reports with advanced filtering
              options
            </Typography>
          </CardContent>
        </Card>

        {/* Filters Section */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
              Filter Options
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2,
                mb: 3,
              }}>
              <TextField
                label="Reference Code"
                value={filters.product_id || ""}
                onChange={handleFilterChange("product_id")}
                inputProps={{
                  inputMode: "text",
                }}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Description"
                value={filters.description}
                onChange={handleFilterChange("description")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <Autocomplete
                fullWidth
                size="small"
                options={modelTypes}
                getOptionLabel={(option) => option.description}
                value={selectedOEMType}
                onChange={(event, newValue) => {
                  setSelectedOEMType(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="OEM Type"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status ?? ""}
                  onChange={(event: SelectChangeEvent<0 | 1>) => {
                    setFilters({
                      ...filters,
                      status: event.target.value as 0 | 1 | undefined,
                    });
                    setCurrentPage(1);
                  }}
                  label="Status"
                  sx={{
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                  }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={1}>Enabled</MenuItem>
                  <MenuItem value={0}>Disabled</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Created By"
                value={filters.created_by}
                onChange={handleFilterChange("created_by")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Product Type</InputLabel>
                <Select
                  value={filters.product_type}
                  onChange={handleFilterChange("product_type")}
                  label="Product Type"
                  sx={{
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                  }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Product">Product</MenuItem>
                  <MenuItem value="Service">Service</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={filters.business_type}
                  onChange={handleFilterChange("business_type")}
                  label="Business Type"
                  sx={{
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                  }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="2W">2W</MenuItem>
                  <MenuItem value="3W">3W</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{
                  borderColor: "#000",
                  color: "#000",
                  "&:hover": {
                    borderColor: "#000",
                    bgcolor: "#f5f5f5",
                  },
                }}>
                Clear Filters
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#333",
                  },
                }}>
                Search
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={loading}
                sx={{
                  bgcolor: "#fff",
                  color: "#000",
                  border: "2px solid #000",
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                  },
                  "&:disabled": {
                    bgcolor: "#e0e0e0",
                    color: "#999",
                  },
                }}>
                Download Excel
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, border: "1px solid #ff5252" }}
            onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <Card sx={{ boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                  <CircularProgress sx={{ color: "#000" }} />
                </Box>
              ) : (
                <>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Reference Code
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Description
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Cost Price
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Selling Price
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Taxes
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          OEM Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Vendor Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Product Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Business Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Element Type
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Lead Time (Days)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>HSN Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Created At
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Created By
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Part Family
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Part Location
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={16} align="center" sx={{ py: 5 }}>
                            <Typography color="text.secondary">
                              No records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.map((row) => (
                          <TableRow
                            key={row.reference_code}
                            sx={{
                              "&:hover": { bgcolor: "#fafafa" },
                              "&:last-child td": { border: 0 },
                            }}>
                            <TableCell>{row.reference_code}</TableCell>
                            <TableCell>
                              <Tooltip title={row.product_description}>
                                <Typography
                                  sx={{
                                    maxWidth: 150,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}>
                                  {row.product_description}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(row.cost_price)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(row.selling_price)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(row.tax)}
                            </TableCell>
                            <TableCell>{row.oem_type}</TableCell>
                            <TableCell>{row.vendor_type}</TableCell>
                            <TableCell>{row.product_type}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.business_type}
                                size="small"
                                sx={{
                                  bgcolor: "#f5f5f5",
                                  color: "#000",
                                  border: "1px solid #e0e0e0",
                                }}
                              />
                            </TableCell>
                            <TableCell>{row.element_type}</TableCell>
                            <TableCell align="right">
                              {row.lead_time_in_days}
                            </TableCell>
                            <TableCell>{row.hsn_code}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.status}
                                size="small"
                                sx={{
                                  bgcolor:
                                    row.status === "Enabled"
                                      ? "#000"
                                      : "#e0e0e0",
                                  color:
                                    row.status === "Enabled" ? "#fff" : "#666",
                                }}
                              />
                            </TableCell>
                            <TableCell>{row.created_at}</TableCell>
                            <TableCell>{row.created_by}</TableCell>
                            <TableCell>{row?.part_family}</TableCell>
                            <TableCell>{row?.part_location}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      borderTop: "1px solid #e0e0e0",
                      bgcolor: "#fafafa",
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2">Rows per page:</Typography>
                      <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        size="small"
                        sx={{
                          minWidth: 80,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                        }}>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                      </Select>
                    </Box>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      showFirstButton
                      showLastButton
                      sx={{
                        "& .MuiPaginationItem-root": {
                          "&.Mui-selected": {
                            bgcolor: "#000",
                            color: "#fff",
                            "&:hover": {
                              bgcolor: "#333",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default CreatedProductReport;